import { FC, useEffect, useState } from "react";
import {
  Box,
  Checkbox,
  FormControlLabel,
  RadioGroup,
  TextField,
  Typography,
  Radio,
  FormHelperText,
} from "@mui/material";
import { ControlledSelect, InputDialog, makeSxStyles } from "ui";
import { notificationsDeliverySelectOptions } from "service";
import { Edit } from "@mui/icons-material";
import {
  notificationsRecurrenceSelectOptions,
  notificationsRepeatSelectOptions,
} from "service/types/enum/notifications-enum";

import { DateTime } from "luxon";
import { DateTimePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterLuxon } from "@mui/x-date-pickers/AdapterLuxon";
import { formattedRecurrence, weekdays } from "../../../utils/string-helpers";
import { AdminPutNotificationReqDto } from "service/api/estabrookSchemas";
import { ExtendedFormProps } from "../../../shared/types/formTypes";
import FormField from "../../../shared/components/FormField";
import FormLayout from "../../../shared/components/FormLayout";
import NotificationTypeSelector from "../../../shared/components/NotificationTypeSelector";

const NotificationForm: FC<ExtendedFormProps<AdminPutNotificationReqDto>> = ({
  values,
  handleChange,
  handleBlur,
  touched,
  errors,
  setFieldValue,
  setFieldTouched,
}) => {
  const styles = useStyles();
  const [open, setOpen] = useState(false);
  const [radioValue, setRadioValue] = useState(values.endDate ? "on" : "never");
  const [dateValue, setDateValue] = useState<DateTime | null>();

  useEffect(() => {
    if (values.endDate) {
      setRadioValue("on");
    }
  }, [values.endDate]);

  useEffect(() => {
    if (values.date) {
      setDateValue(DateTime.fromISO(values.date));
    }
  }, [values.date]);

  const handleNotificationTypeChange = (newValue: string[]) => {
    setFieldValue("type", newValue);
  };

  const handleDeliveryTypeChange = (value: string) => {
    setFieldValue("deliveryType", value);
    setFieldTouched?.("deliveryType", true);
    if (value !== "recurring") {
      setFieldValue("recurrence", undefined);
    } else {
      setFieldValue("recurrence", "daily");
    }
    if (value === "instant") {
      setFieldValue("date", undefined);
      setDateValue(null);
    }
  };

  const handleRecurrenceChange = (value: string) => {
    setFieldValue("recurrence", value);
    setFieldTouched?.("recurrence", true);
    if (value === "custom") {
      setFieldValue("customInterval", 1);
      setFieldValue("customPeriod", "day");
      setFieldValue("customWeekday", []);
      setOpen(true);
    } else {
      setFieldValue("customInterval", undefined);
      setFieldValue("customPeriod", undefined);
      setFieldValue("customWeekday", undefined);
    }
  };

  const handleDateTimeChange = (value: DateTime | null) => {
    setDateValue(value);
    setFieldValue("date", value?.toUTC().toISO());
    setFieldTouched?.("date", true);
  };

  return (
    <>
      <FormLayout>
        <FormField
          label="Title"
          id="title"
          placeholder="Enter title"
          value={values.title}
          onChange={handleChange}
          onBlur={handleBlur}
          touched={touched}
          errors={errors}
        />

        <FormField
          label="Notification Type"
          id="type"
          touched={touched}
          errors={errors}
          labelProps={{ fontWeight: 600, alignSelf: "flex-start" }}
        >
          <NotificationTypeSelector
            value={values.type}
            onChange={handleNotificationTypeChange}
          />
        </FormField>

        <FormField
          label="Delivery"
          id="deliveryType"
          touched={touched}
          errors={errors}
        >
          <ControlledSelect
            placeholderSelectEnabled={false}
            placeholdeShow={false}
            placeholder="Delivery Type"
            options={notificationsDeliverySelectOptions}
            value={values.deliveryType}
            onChange={handleDeliveryTypeChange}
            data-testid="delivery-type-select"
          />
        </FormField>

        <FormField
          label="Date and Time"
          id="dateTime"
          touched={touched}
          errors={errors}
          labelProps={{ fontWeight: 600, alignSelf: "flex-start" }}
        >
          <div data-testid="date-time-picker-container">
            <LocalizationProvider dateAdapter={AdapterLuxon}>
              <DateTimePicker
                disabled={values.deliveryType === "instant"}
                sx={{ width: "100%" }}
                value={dateValue}
                onChange={handleDateTimeChange}
                minDateTime={DateTime.now().plus({ minutes: 5 })}
                minutesStep={5}
              />
            </LocalizationProvider>
            <ControlledSelect
              data-testid="recurrence-select"
              disabled={values.deliveryType !== "recurring"}
              style={{ marginTop: "16px" }}
              placeholderSelectEnabled={false}
              placeholdeShow={false}
              placeholder="Does not repeat"
              options={notificationsRecurrenceSelectOptions}
              value={values.recurrence}
              onChange={handleRecurrenceChange}
            />
            {values.recurrence === "custom" && (
              <Box sx={{ position: "relative" }}>
                <Typography style={{ marginTop: "16px", marginRight: "24px" }}>
                  {formattedRecurrence(values)}
                </Typography>
                <Edit
                  className="toShow"
                  onClick={() => setOpen(true)}
                  style={{
                    cursor: "pointer",
                    position: "absolute",
                    right: "0",
                    top: "0",
                  }}
                />
              </Box>
            )}
          </div>
        </FormField>

        <FormField
          label="Message"
          id="description"
          placeholder="Enter message"
          value={values.description}
          onChange={handleChange}
          onBlur={handleBlur}
          touched={touched}
          errors={errors}
          multiline
          rows={5}
          labelProps={{ fontWeight: 600, alignSelf: "flex-start" }}
        />
      </FormLayout>

      {open && (
        <InputDialog
          open={open}
          sx={styles.dialog}
          onClose={async () => setOpen(false)}
          title="Set Custom Recurrence"
          confirmText="Save"
          onConfirm={async (onClose) => onClose()}
        >
          <>
            <Box sx={styles.flexContainer}>
              <Typography>Repeat every:</Typography>
              <TextField
                id="customInterval"
                type="number"
                value={values.customInterval}
                onChange={handleChange}
                onBlur={handleBlur}
                helperText={
                  touched.customInterval && (errors.customInterval as string)
                }
                error={touched.customInterval && !!errors.customInterval}
                margin="dense"
                variant="outlined"
                style={{ width: "80px" }}
              />
              <ControlledSelect
                placeholderSelectEnabled={false}
                placeholdeShow={false}
                placeholder="Days"
                options={notificationsRepeatSelectOptions}
                value={values.customPeriod}
                onChange={(value) => {
                  setFieldValue("customPeriod", value);
                  setFieldTouched?.("customPeriod", true);
                  if (value !== "week") {
                    setFieldValue("customWeekday", []);
                  }
                }}
              />
            </Box>
            {values.customPeriod === "week" && (
              <>
                <Typography style={{ marginBottom: "16px" }}>
                  Repeat on
                </Typography>
                <Box sx={styles.flexContainer} position="relative">
                  {values.customWeekday &&
                    weekdays.map((weekday) => (
                      <FormControlLabel
                        style={{ marginLeft: "0px" }}
                        key={weekday.value}
                        control={
                          <Checkbox
                            icon={<span>{weekday.short}</span>}
                            checkedIcon={<span>{weekday.short}</span>}
                            sx={styles.dayCheckbox}
                            name="customWeekday"
                            value={weekday.value}
                            onChange={(event) => {
                              if (event.target.checked) {
                                setFieldValue("customWeekday", [
                                  ...values.customWeekday!,
                                  weekday.value,
                                ]);
                              } else {
                                setFieldValue(
                                  "customWeekday",
                                  (values.customWeekday as number[])!.filter(
                                    (value) => value !== weekday.value,
                                  ),
                                );
                              }
                              setFieldTouched?.("customWeekday", true);
                            }}
                            checked={values.customWeekday?.includes(
                              weekday.value,
                            )}
                          />
                        }
                        label=""
                      />
                    ))}
                  {touched.customWeekday && (
                    <FormHelperText error>
                      {errors.customWeekday as string}
                    </FormHelperText>
                  )}
                </Box>
              </>
            )}
            <Typography style={{ margin: "16px 0" }}>Ends</Typography>
            <RadioGroup
              defaultValue="never"
              value={radioValue}
              onChange={(event) => {
                setRadioValue(event.target.value);
                if (event.target.value === "never") {
                  setFieldValue("endDate", undefined);
                } else {
                  setFieldValue("endDate", DateTime.now().toISODate());
                }
              }}
            >
              <FormControlLabel
                value="never"
                control={<Radio />}
                label="Never"
              />
              <FormControlLabel
                value="on"
                control={<Radio />}
                label={
                  <Box
                    display="flex"
                    flexDirection="row"
                    alignItems="center"
                    gap="40px"
                  >
                    <Typography>On</Typography>
                    <TextField
                      disabled={radioValue === "never"}
                      id="endDate"
                      type="date"
                      value={values.endDate}
                      onChange={(event) => {
                        setFieldValue("endDate", event.target.value);
                        setFieldTouched?.("endDate", true);
                      }}
                      onBlur={handleBlur}
                      helperText={touched.endDate && (errors.endDate as string)}
                      error={touched.endDate && !!errors.endDate}
                      margin="dense"
                      variant="outlined"
                    />
                  </Box>
                }
              />
            </RadioGroup>
          </>
        </InputDialog>
      )}
    </>
  );
};

const useStyles = makeSxStyles(() => ({
  flexContainer: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    gap: "12px",
  },
  dayCheckbox: {
    position: "relative",
    minWidth: "38px",
    "&:before": {
      position: "absolute",
      content: "''",
      display: "inline-block",
      top: "1px",
      width: "38px",
      height: "38px",
      borderRadius: "100px",
      border: "1px solid #8B9198",
    },
    "&.Mui-checked": {
      "&:before": {
        backgroundColor: "#004b3b40",
        borderColor: "#005140",
      },
    },
  },
  dialog: {
    ".MuiDialog-paper": {
      minWidth: "500px",
    },
  },
}));

export default NotificationForm;
